import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import CameraForm from "../components/CameraForm";
import LineCalibrationCanvas from "../components/LineCalibrationCanvas";
import { toast } from "react-toastify";
import {
  startProcessing,
  getLineCalibration,
  analyzeAudio,
  startRtspAudioProcessing,
  requestWebSocketAudioSlot,
  requestMicStream,
  analyzeBounceAudio,
  startRtspBounceAudioProcessing,
  PREDEFINED_MODES_MAP,
  getAllInstancesStatus,
  registerCamera,
} from "../Api";
import './AddCamera.css'
const CALIB_MODES = [
  "Footfall Tracker",
  "Queue Monitor",
  "Idle Employee Tracker",
  "Traffic Analyzer",
  "Waiting Time Tracker",
];

const PROCESSING_DIMENSIONS = {
  "People Counting": { width: 1020, height: 600 },
  "Queue Management": { width: 1280, height: 720 },
  "Idle Employee Counter": { width: 1280, height: 720 },
  "Vehicle Counting & LP Rec": { width: 1280, height: 720 },
  "Waiting Time Tracker": { width: 1280, height: 720 },
};

export default function AddCamera() {
  const navigate = useNavigate();

  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationStreamUrl, setCalibrationStreamUrl] = useState(null);
  const [calibrationMode, setCalibrationMode] = useState(null);
  const [calibrationSourcePath, setCalibrationSourcePath] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [calibratingInstanceId, setCalibratingInstanceId] = useState(null);
  const [activeVideoInstances, setActiveVideoInstances] = useState([]);
  const [formForRegistration, setFormForRegistration] = useState(null);

  /* ─────────────────────────── initial fetch ────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        const resp = await getAllInstancesStatus();
        if (resp?.instances?.length) {
          setActiveVideoInstances(
            resp.instances.filter(
              (i) =>
                i.mode !== "Audio Sentiment Analysis" &&
                i.mode !== "Bounce Audio Analysis" &&
                i.mode !== "AI Detection"
            )
          );
        }
      } catch (err) {
        console.error("[AddCamera] Initial status fetch failed:", err);
      }
    })();
  }, []);

  /* helpers ------------------------------------------------------------ */
  const getModeSlug = (backendMode) =>
    backendMode === "People Counting"
      ? "people"
      : backendMode === "Queue Management"
      ? "queue"
      : backendMode === "Idle Employee Counter"
      ? "idle_area"
      : backendMode === "Vehicle Counting & LP Rec"
      ? "vehicle"
      : backendMode === "Waiting Time Tracker"
      ? "waiting_time"
      : null;

  /* ─────────────────────────── on form submit ───────────────────────── */
  const handleFormSubmit = async (form) => {
    setIsLoading(true);
    setIsCalibrating(false);
    setCalibrationMode(null);
    setCalibrationStreamUrl(null);
    setCalibrationSourcePath(null);
    setCalibratingInstanceId(null);
    setFormForRegistration(null);

    const persistentSourcePathForRegistration =
      form.source_type === "live_mic"
        ? `live_mic_source_${form.camera_name.replace(/\s+/g, "_").toLowerCase()}`
        : form.uploaded_path || form.source_path;

    try {
      if (!form.selected_mode || !form.analysis_category) {
        throw new Error("Internal error: mode/category missing.");
      }

      let instanceDataForNav = null;
      let processingStarted = false;

      /* ──── Customer Service Monitor ─────────────────────── */
      if (form.selected_mode === "Audio Sentiment Analysis") {
        let audioResp;
        toast.info(`Starting CSM via ${form.source_type}…`);

        if (form.source_type === "live_mic") {
          await requestMicStream(form.camera_name);

          const wsPayload = {
            camera_display_name: form.camera_name,
            analysis_mode: form.analysis_mode || "Customer_sentiment_analyzer"
          };
          if (form.camera_location?.trim()) {
            wsPayload.camera_location = form.camera_location.trim();
          }
          if (form.mic_video_rtsp_url?.trim()) {
            wsPayload.rtsp_url = form.mic_video_rtsp_url.trim();
          }

          audioResp = await requestWebSocketAudioSlot(wsPayload);
          if (!audioResp?.stream_id) {
            throw new Error(
              audioResp?.message ||
                "Failed to request WebSocket slot for Live-Mic."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "websocket_audio",
            audioStreamId: audioResp.stream_id,
            audioOriginalSource: form.camera_name,
            micDisplayName: form.camera_name,
            micLocation: form.camera_location,
            audioWebSocketUrl: audioResp.websocket_url,
            audioVideoStreamUrl: audioResp.video_stream_url,
            logFile: audioResp.log_file,
          };
        } else if (form.source_type === "rtsp") {
          const payload = { 
            rtsp_url: form.source_path,
            analysis_mode: form.analysis_mode || "Customer_sentiment_analyzer"
          };
          // Generate mode-specific readable stream IDs
          if (form.camera_name?.trim()) {
            const baseId = form.camera_name.trim();
            const modeId = (form.analysis_mode || "Customer_sentiment_analyzer")
              .toLowerCase().replace(/_/g, '-');
            payload.stream_id = `${baseId}_${modeId}`;
          }
          if (form.camera_location?.trim())
            payload.camera_location = form.camera_location.trim();

          audioResp = await startRtspAudioProcessing(payload);
          if (!audioResp?.stream_id) {
            throw new Error(
              audioResp?.message || "Failed to start RTSP sentiment."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "rtsp",
            audioStreamId: audioResp.stream_id,
            audioOriginalSource: form.source_path,
            audioVideoStreamUrl: audioResp.video_stream_url,
            logFile: audioResp.log_file,
          };
        } else if (form.file_obj_from_form) {
          const payload = { file_to_upload: form.file_obj_from_form };
          if (form.camera_location?.trim())
            payload.camera_location = form.camera_location.trim();

          audioResp = await analyzeAudio(payload);
          if (!audioResp?.stream_id) {
            throw new Error(
              audioResp?.message || "Failed to start local sentiment."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "local_chunked",
            audioStreamId: audioResp.stream_id,
            audioOriginalSource:
              audioResp.original_file_path || form.file_obj_from_form.name,
            logFile: audioResp.log_file,
            playbackFileObject: form.file_obj_from_form,
          };
        } else {
          const trimmed = form.source_path.trim();
          const payload = {
            s3_http_url: trimmed,
            force_chunked_processing: true,
          };
          if (form.camera_location?.trim())
            payload.camera_location = form.camera_location.trim();

          audioResp = await analyzeAudio(payload);
          if (!audioResp?.stream_id) {
            throw new Error(
              audioResp?.message || "Failed to start remote sentiment."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "remote_chunked",
            audioStreamId: audioResp.stream_id,
            audioOriginalSource: audioResp.original_file_path || trimmed,
            logFile: audioResp.log_file,
            playbackRemoteUrl: trimmed,
          };
        }

        processingStarted = true;
      }

      /* ──── Bounce Detector ──────────────────────────────── */
      else if (form.selected_mode === "Bounce Audio Analysis") {
        let bResp;
        const srcLabel =
          form.source_type === "live_mic"
            ? "Live-Mic"
            : form.source_type === "rtsp"
            ? "RTSP"
            : "file/link";
        toast.info(`Starting Bounce Detector for ${srcLabel}…`);

        if (form.source_type === "live_mic") {
          await requestMicStream(form.camera_name);

          const wsPayload = {
            camera_display_name: form.camera_name,
            analysis_mode: "Bounce Detector",
          };
          if (form.camera_location?.trim()) {
            wsPayload.camera_location = form.camera_location.trim();
          }
          if (form.mic_video_rtsp_url?.trim()) {
            wsPayload.rtsp_url = form.mic_video_rtsp_url.trim();
          }

          bResp = await requestWebSocketAudioSlot(wsPayload);
          if (!bResp?.stream_id) {
            throw new Error(
              bResp?.message ||
                "Failed to request WebSocket slot for Bounce Live-Mic."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "websocket_audio_bounce",
            audioStreamId: bResp.stream_id,
            audioOriginalSource: form.camera_name,
            micDisplayName: form.camera_name,
            micLocation: form.camera_location,
            audioWebSocketUrl: bResp.websocket_url,
            audioVideoStreamUrl: bResp.video_stream_url,
            logFile: bResp.log_file,
          };
        } else if (form.source_type === "rtsp") {
          const payload = { rtsp_url: form.source_path };
          // Generate readable stream ID for bounce detector
          if (form.camera_name?.trim()) {
            payload.stream_id = form.camera_name.trim();
          }
          if (form.camera_location?.trim())
            payload.camera_location = form.camera_location.trim();

          bResp = await startRtspBounceAudioProcessing(payload);
          if (!bResp?.stream_id) {
            throw new Error(
              bResp?.message || "Failed to start RTSP bounce audio."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "rtsp_bounce",
            audioStreamId: bResp.stream_id,
            audioOriginalSource: form.source_path,
            audioVideoStreamUrl: bResp.video_stream_url,
            logFile: bResp.log_file,
          };
        } else if (form.file_obj_from_form) {
          const payload = { file_to_upload: form.file_obj_from_form };
          if (form.camera_location?.trim())
            payload.camera_location = form.camera_location.trim();

          bResp = await analyzeBounceAudio(payload);
          if (!bResp?.stream_id) {
            throw new Error(
              bResp?.message || "Failed to start local bounce audio."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "local_chunked_bounce",
            audioStreamId: bResp.stream_id,
            audioOriginalSource:
              bResp.original_file_path || form.file_obj_from_form.name,
            logFile: bResp.log_file,
            playbackFileObject: form.file_obj_from_form,
          };
        } else {
          const trimmed = form.source_path.trim();
          const payload = {
            s3_http_url: trimmed,
            force_chunked_processing: true,
          };
          if (form.camera_location?.trim())
            payload.camera_location = form.camera_location.trim();

          bResp = await analyzeBounceAudio(payload);
          if (!bResp?.stream_id) {
            throw new Error(
              bResp?.message || "Failed to start remote bounce audio."
            );
          }

          instanceDataForNav = {
            activeMode: form.analysis_category,
            audioTaskType: "remote_chunked_bounce",
            audioStreamId: bResp.stream_id,
            audioOriginalSource: bResp.original_file_path || trimmed,
            logFile: bResp.log_file,
            playbackRemoteUrl: trimmed,
          };
        }

        processingStarted = true;
      }

      /* ──── VIDEO OR AI DETECTION AGENT ───────────────────── */
      else {
        const srcPath = form.uploaded_path || form.source_path;
        if (!srcPath) throw new Error("Source path missing.");

        const camId =
          form.camera_name || `cam-${Date.now().toString().slice(-6)}`;

        const startPayload = {
          cameras_list: [
            {
              camera_id: camId,
              source_type: form.source_type,
              source_path: srcPath,
            },
          ],
          selected_mode: form.selected_mode,
        };
        if (
          form.analysis_category === "AI Detection Agent" &&
          form.custom_prompt_text
        ) {
          startPayload.custom_prompt_text = form.custom_prompt_text;
        }

        const multiResp = await startProcessing(startPayload);
        if (multiResp.previous_process_stopped) {
          toast.info("A previous analysis process was stopped.");
        }
        if (!multiResp.instances?.length) {
          throw new Error(
            multiResp.overall_message || "Failed to start processing."
          );
        }

        const inst = multiResp.instances[0];
        if (
          !["started", "already_running_this_source_mode", "already_running"].includes(
            inst.status
          )
        ) {
          throw new Error(inst.message || "Instance failed to start.");
        }

        toast.success(
          `${inst.status === "started" ? "Started" : "Found existing"} ${
            form.analysis_category
          } for ${inst.camera_id}.`
        );

        const needsCalib =
          CALIB_MODES.includes(form.analysis_category) &&
          form.analysis_category !== "AI Detection Agent";

        if (needsCalib && inst.stream_url) {
          try {
            const fresh = await getAllInstancesStatus();
            if (fresh?.instances)
              setActiveVideoInstances(
                fresh.instances.filter(
                  (i) =>
                    i.mode !== "Audio Sentiment Analysis" &&
                    i.mode !== "Bounce Audio Analysis" &&
                    i.mode !== "AI Detection"
                )
              );
          } catch (e) {
            console.warn("Status refresh before calib failed:", e);
          }

          const modeSlug = getModeSlug(form.selected_mode);
          if (!modeSlug || !PROCESSING_DIMENSIONS[form.selected_mode]) {
            throw new Error("Calibration config missing.");
          }

          try {
            await getLineCalibration(modeSlug, srcPath);
          } catch (e) {
            if (e.status === 404) {
              setFormForRegistration(form);
              setCalibrationMode(form.selected_mode);
              setCalibrationStreamUrl(inst.stream_url);
              setCalibrationSourcePath(srcPath);
              setCalibratingInstanceId(inst.instance_id);
              setIsCalibrating(true);
              setIsLoading(false);
              return;
            }
            throw e;
          }
        }

        instanceDataForNav = {
          activeMode: form.analysis_category,
          newlyStartedInstance: inst,
        };
        processingStarted = true;
      }

      /* ────────── Registration (new cameras / mics) ───────── */
      if (
        processingStarted &&
        !form.is_existing_camera_from_form &&
        form.camera_name &&
        persistentSourcePathForRegistration &&
        form.analysis_category !== "AI Detection Agent"
      ) {
        const isVideoMode = ![
          "Audio Sentiment Analysis",
          "Bounce Audio Analysis",
        ].includes(form.selected_mode);

        const canRegister =
          isVideoMode ||
          form.source_type === "rtsp" ||
          form.source_type === "live_mic";

        if (canRegister) {
          try {
            const regPayload = {
              name: form.camera_name,
              url: persistentSourcePathForRegistration,
              agent: form.analysis_category,
              location: form.camera_location || "",
            };

            // Add analysis_mode for Customer Service Monitor
            if (form.analysis_category === "Customer Service Monitor" && form.analysis_mode) {
              regPayload.analysis_mode = form.analysis_mode;
            }

            const regResp = await registerCamera(regPayload);
            toast.success(
              `Source "${regResp.name}" registered for "${regResp.agent}".`
            );
          } catch (regErr) {
            if (regErr.status === 409) {
              toast.warn(
                regErr.data?.detail || regErr.message || "Registration conflict."
              );
            } else {
              toast.error(
                regErr.data?.detail || regErr.message || "Registration failed."
              );
            }
          }
        }
      }

      /* ────────── Navigate to View page ───────── */
      if (instanceDataForNav) navigate("/view", { state: instanceDataForNav });
      else navigate("/view");
    } catch (err) {
      console.error("[AddCamera] submit error:", err);
      toast.error(err.message || "Error starting analysis.");
    } finally {
      setIsLoading(false);
    }
  };

  /* ─────────────────────── calibration complete ─────────────────────── */
  const handleCalibrationComplete = async () => {
    const origForm = formForRegistration;
    setIsCalibrating(false);
    setIsLoading(true);

    const displayNameForView =
      Object.keys(PREDEFINED_MODES_MAP).find(
        (k) => PREDEFINED_MODES_MAP[k] === calibrationMode
      ) || calibrationMode;

    if (
      origForm &&
      !origForm.is_existing_camera_from_form &&
      origForm.camera_name &&
      calibrationSourcePath &&
      origForm.analysis_category !== "AI Detection Agent"
    ) {
      try {
        const regPayload = {
          name: origForm.camera_name,
          url: calibrationSourcePath,
          agent: origForm.analysis_category,
          location: origForm.camera_location || "",
        };
        // Add analysis_mode for Customer Service Monitor
        if (origForm.analysis_category === "Customer Service Monitor" && origForm.analysis_mode) {
          regPayload.analysis_mode = origForm.analysis_mode;
        }
        await registerCamera(regPayload);
        toast.success(
          `Source "${origForm.camera_name}" registered post-calibration.`
        );
      } catch (e) {
        if (e.status === 409) {
          toast.warn(e.data?.detail || "Already registered.");
        } else {
          toast.error(e.data?.detail || "Registration failed.");
        }
      }
    }

    navigate("/view", {
      state: {
        activeMode: displayNameForView,
        justCalibratedInstance: {
          instance_id: calibratingInstanceId,
          stream_url: calibrationStreamUrl,
          mode: calibrationMode,
          input_source_path: calibrationSourcePath,
          camera_id:
            activeVideoInstances.find(
              (i) => i.instance_id === calibratingInstanceId
            )?.camera_id || origForm?.camera_name || "N/A",
        },
      },
    });

    setCalibrationMode(null);
    setCalibrationStreamUrl(null);
    setCalibrationSourcePath(null);
    setCalibratingInstanceId(null);
    setFormForRegistration(null);
    setIsLoading(false);
  };

  const dims = calibrationMode ? PROCESSING_DIMENSIONS[calibrationMode] : null;
  const slug = calibrationMode ? getModeSlug(calibrationMode) : null;

  /* ───────────────────────────── render ─────────────────────────────── */
  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-section">
        <div className="page-card">
          {!isCalibrating ? (
            <>
              <h1 style={{ marginBottom: "1rem" }}>
                Add Source &amp; Start Agent
              </h1>
              <CameraForm onSubmit={handleFormSubmit} isLoading={isLoading} />
            </>
          ) : calibrationMode && dims && calibrationStreamUrl && slug ? (
            <LineCalibrationCanvas
              streamUrl={calibrationStreamUrl}
              onSaveComplete={handleCalibrationComplete}
              processingWidth={dims.width}
              processingHeight={dims.height}
              modeSlug={slug}
              sourcePath={calibrationSourcePath}
            />
          ) : (
            <div>
              <p className="status-message">Loading calibration setup…</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}